import React, {useEffect, useMemo, useRef, useState} from "react";
import {Button} from "@/shared/components/ui/button";
import {ArrowUpWideNarrow} from "lucide-react";
import {Table} from "@tanstack/react-table";
import {Checkbox} from "@/shared/components/ui/checkbox";

interface ColumnVisibilityTogglerProps<TData> {
    table: Table<TData>;
}

export function ColumnVisibilityToggler<TData>({table}: ColumnVisibilityTogglerProps<TData>) {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const toggleMenu = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsOpen((prev) => !prev);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const hideableColumns = useMemo(
        () => table.getAllColumns().filter((col) => col.getCanHide() && col.columnDef.meta?.label),
        [table]
    );

    const allVisible = hideableColumns.every((col) => col.getIsVisible());

    return (
        <div className="relative">
            <Button
                variant="outline"
                size="sm"
                onClick={toggleMenu}
                className="flex items-center gap-2"
            >
                <ArrowUpWideNarrow/>
            </Button>

            {isOpen && (
                <div
                    ref={ref}
                    className="absolute right-0 mt-2 w-56 bg-white border rounded-md shadow-md z-50 p-3"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex flex-col gap-2">
                        <div className="flex flex-col gap-1 max-h-64 overflow-y-auto">
                            {hideableColumns.map((column) => (
                                <label
                                    key={column.id}
                                    className="flex items-center text-sm gap-2 hover:bg-gray-50 p-1 rounded cursor-pointer"
                                >
                                    <Checkbox
                                        checked={column.getIsVisible()}
                                        onCheckedChange={(checked) =>
                                            column.toggleVisibility(checked as boolean)
                                        }
                                    />
                                    {column.columnDef.meta?.label}
                                </label>
                            ))}
                        </div>
                        <div className="pt-2 border-t border-gray-200">
                            <label
                                className="flex items-center text-sm font-medium gap-2 hover:bg-gray-50 p-1 rounded cursor-pointer">
                                <Checkbox
                                    checked={allVisible}
                                    onCheckedChange={(checked) =>
                                        hideableColumns.forEach((col) => col.toggleVisibility(checked as boolean))
                                    }
                                />
                                Pokaż wszystkie
                            </label>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}